// Types
interface User {
  username: string;
  [key: string]: unknown;
}

interface Post {
  _id: string;
  attachments?: Array<{
    media?: {
      type?: string;
    };
  }>;
  [key: string]: unknown;
}

interface FeedItem {
  loading?: boolean;
  list?: string[];
  posts?: string[];
  [key: string]: unknown;
}

interface LinkPreviewPost {
  _id: string;
}

interface RootState {
  Users: Record<string, User>;
  User: User & {
    linkPreviewPosts?: LinkPreviewPost[];
    purchases?: unknown[];
  };
  Posts: Record<string, Post>;
  Feed: Record<string, FeedItem>;
  [key: string]: unknown;
}

interface ReducerParams {
  cursor?: string;
  reducer: string;
}

export const getUsersListByIdsSelector = (
  { Users }: Pick<RootState, 'Users'>,
  arr: string[],
): (User | undefined)[] => arr.map((username) => Users[username]);

export const getUsersListByReducerSelector = (
  { Users, User, ...state }: RootState,
  { cursor, reducer }: ReducerParams,
): { loading: boolean; list: (User | undefined)[] } => {
  const defaultCursor = cursor || User.username;
  const reducerState = (state as Record<string, Record<string, FeedItem>>)[
    reducer
  ];
  const { loading = false, list = [] } = reducerState?.[defaultCursor] || {};

  return {
    loading,
    list: list.map((username) => Users[username]),
  };
};

export const getPostsListByReducerSelector = (
  { Posts, Feed }: Pick<RootState, 'Posts' | 'Feed'>,
  cursor: string,
): {
  loading: boolean;
  posts: Post[];
  hasNext?: boolean;
  [key: string]: unknown;
} => {
  const { loading = false, posts = [], hasNext, ...rest } = Feed[cursor] || {};
  return {
    ...rest,
    loading,
    hasNext: hasNext as boolean | undefined,
    posts: (posts as string[])
      .map((_id) => Posts[_id])
      .filter((post): post is Post => !!post),
  };
};

export const getLivePostByReducerSelector = (
  { Posts, Feed }: Pick<RootState, 'Posts' | 'Feed'>,
  cursor: string,
): { liveStream: Post | undefined } => {
  const {
    loading: _loading = false,
    posts: _posts = [],
    ..._rest
  } = Feed[cursor] || {};

  const liveStreamKey = Object.keys(Posts).find(
    (postKey) => Posts[postKey]?.attachments?.[0]?.media?.type === 'stream',
  );

  return {
    liveStream: liveStreamKey ? Posts[liveStreamKey] : undefined,
  };
};

export const getLinkPreviewPostsBySelector = ({
  User,
  Posts,
}: Pick<RootState, 'User' | 'Posts'>): Post[] => {
  const { linkPreviewPosts = [] } = User || {};

  return linkPreviewPosts
    .map(({ _id }) => Posts[_id])
    .filter((post): post is Post => !!post);
};

export const getPurchases = (state: RootState): unknown[] | undefined =>
  state?.User?.purchases;

export const getUserName = (state: RootState): string | undefined =>
  state?.User?.username;
